<template>
    <div class="card">
        <PageHeader
            :showBtn="false"
            title="Receipt Payment Ledger"
        />

        <div class="px-2 mt-2 d-flex">
          <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo" class="flex-grow-1">
            <div class="col-auto d-flex align-items-end mt-2">
              <FilterPopover
                  ref="filterPopoverRef"
                  title="Filters"
                  :active-count="activeFilterCount"
                  @open="onOpenFilter"
                  @apply="onApplyFilters"
                  @clear="onClearFilters"
              >
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Account Head</label>
                  <v-select
                      placeholder="Select Account Head"
                      :options="accountHeads"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.account_head_id"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Cash/Bank Head</label>
                  <v-select
                      placeholder="Select Cash/Bank Head"
                      :options="cashAndBankHeads"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.mop_account_head_id"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Voucher Type</label>
                  <v-select
                      placeholder="Select Voucher Type"
                      :options="bucketOptions"
                      label="name"
                      :reduce="option => option.value"
                      v-model="draftFilters.bucket"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Party</label>
                  <AsyncSelect
                      placeholder="Select Party"
                      v-model="draftFilters.contact_profile"
                      :api-service="fetchContactProfiles"
                      :format-label="formatPatientLabel"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Business</label>
                  <v-select
                      placeholder="Select Business"
                      :options="businesses"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.business_id"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Cost Centre</label>
                  <v-select
                      placeholder="Select Cost Centre"
                      :options="costCentres"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.cost_centre_id"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Project</label>
                  <v-select
                      placeholder="Select Project"
                      :options="projects"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.project_id"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Txn Type</label>
                  <select class="form-control" v-model="draftFilters.txn_type">
                    <option :value="null">Select txn type</option>
                    <option v-for="type in txnTypeKeys" :value="type" :key="type">
                      {{ homeData.txn_type[type] }}
                    </option>
                  </select>
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Created By</label>
                  <v-select
                      placeholder="Created By"
                      :options="contactList"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.created_by"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <label class="filter-label">Tag Register</label>
                  <v-select
                      placeholder="Select Tag Register"
                      :options="tagRegisters"
                      label="name"
                      :reduce="name => name.id"
                      v-model="draftFilters.tag_register_id"
                  />
                </div>
              </FilterPopover>
            </div>
          </DateQuerySetter>
          <div class="d-flex align-items-end mt-2 ms-2">
            <button
                type="button"
                class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
                @click="exportReport"
            >
              Export
            </button>
          </div>
        </div>

      <div class="mt-1 px-2">
          <div class="table-responsive table-striped my-1 border-bottom" style="min-height: 200px">
            <table class="table table-bordered table-sm-responsive">
              <thead class="table-light">
              <tr>
                <th>Date</th>
                <th>Voucher Number</th>
                <th>Cash/Bank Head</th>
                <th class="text-right">Amount</th>
                <th>Particulars</th>
                <th>Party</th>
                <th>Business</th>
                <th>Project</th>
                <th>Cost Centre</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in tableItems" :key="index">
                <td>{{ item.date }}</td>
                <td>{{ item.voucher_no }}</td>
                <td>{{ item.mop_account_head_name }}</td>
                <td class="text-right">{{ commaFormat(rowAmount(item)) }}</td>
                <td>{{ item.particulars }}</td>
                <td>{{ item.party_name }}</td>
                <td>{{ item.business_name }}</td>
                <td>{{ item.project_name }}</td>
                <td>{{ item.cost_centre_name }}</td>
              </tr>
              </tbody>
              <tfoot v-if="tableItems.length">
              <tr>
                <td colspan="3" class="text-right"><strong>Total</strong></td>
                <td class="text-right"><strong>{{ commaFormat(totalAmount) }}</strong></td>
                <td colspan="5"></td>
              </tr>
              </tfoot>
            </table>
            <p v-if="!tableItems.length" class="mt-5 text-center">No data</p>
          </div>
        </div>

      <div class="px-2">
        <Pagination
            :offset="offset"
            @onPageChange="onPageChange"
            ref="pagination"
        />
      </div>
      <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import Loader from "@/components/atom/LoaderComponent.vue";
import handleReport from "@/services/modules/accountingReport";
import figureFormatter from '@/services/utils/figureFormatter'
import {useStore} from "vuex";
import Pagination from "@/components/atom/Pagination.vue";
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleContact from '@/services/modules/contact'
import handleJournal from "@/services/modules/journal";
import handleDropdowns from "@/services/modules/dropdowns";
import {resetPaginationPage} from '@/services/utils/global.js'

const { getReceiptPaymentLedger } = handleReport();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchAccountHeadsList } = handleJournal();
const { fetchDropdownList } = handleDropdowns();
const route = useRoute()
const router = useRouter();
const store = useStore();
const {commaFormat} = figureFormatter()
const showError = inject('showError');

let offset = ref(25)
const loading = ref(false);
const currentPage = ref(null);
const lastPage = ref(null);
const tableItems = ref([])
const totalAmount = ref('0.00')

const accountHeads = ref([])
const cashAndBankHeads = ref([])
const businesses = ref([])
const projects = ref([])
const costCentres = ref([])
const contactList = ref([])
const tagRegisters = ref([])
const homeData = ref({ txn_type: {} })
const filterPopoverRef = ref(null)

const bucketOptions = computed(() => homeData.value.receipt_payment_buckets || [])

function getInitialFilters () {
  return {
    account_head_id: route.query.account_head_id ? parseInt(route.query.account_head_id) : null,
    mop_account_head_id: route.query.mop_account_head_id ? parseInt(route.query.mop_account_head_id) : null,
    bucket: route.query.bucket || null,
    contact_profile: null,
    business_id: route.query.business_id ? parseInt(route.query.business_id) : null,
    cost_centre_id: route.query.cost_centre_id ? parseInt(route.query.cost_centre_id) : null,
    project_id: route.query.project_id ? parseInt(route.query.project_id) : null,
    txn_type: null,
    created_by: null,
    tag_register_id: null,
  }
}

const appliedFilters = ref(getInitialFilters())
const draftFilters = ref(getInitialFilters())

const txnTypeKeys = computed(() => Object.keys(homeData.value.txn_type))

const activeFilterCount = computed(() => {
  return Object.values(appliedFilters.value).filter((value) => !! value).length
})

function rowAmount(item) {
  if (appliedFilters.value.bucket === 'payment' || appliedFilters.value.bucket === 'contra_payment') {
    return item.debit;
  }
  return item.credit;
}

function getQuery () {
  const filters = appliedFilters.value
  let query = '?company_id=' + route.params.companyId
  query += '&account_head_id=' + filters.account_head_id
  query += '&bucket=' + filters.bucket
  query += '&start_date=' + route.query.start
  query += '&end_date=' + route.query.end
  query += '&offset=' + offset.value
  if (filters.mop_account_head_id) query += '&mop_account_head_id=' + filters.mop_account_head_id
  if (route.query.page) query += '&page=' + route.query.page
  if (filters.contact_profile) {
    query += '&contact_profile_id=' + filters.contact_profile.id
  } else if (route.query.contact_profile_id) {
    query += '&contact_profile_id=' + route.query.contact_profile_id
  }
  if (filters.business_id) query += '&business_id=' + filters.business_id
  if (filters.project_id) query += '&project_id=' + filters.project_id
  if (route.query.donor_id) query += '&donor_id=' + route.query.donor_id
  if (filters.cost_centre_id) query += '&cost_centre_id=' + filters.cost_centre_id
  if (filters.txn_type) query += '&txn_type=' + filters.txn_type
  if (filters.created_by) query += '&created_by=' + filters.created_by
  if (filters.tag_register_id) query += '&tag_register_id=' + filters.tag_register_id
  return query
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    from: data.from,
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const getLedgerData = async () => {
  loading.value = true;
  tableItems.value = [];

  try {
    const res = await getReceiptPaymentLedger(getQuery());

    if (!res.status) {
      showError(res.message);
      tableItems.value = [];
      store.commit('resetPagination');
      return;
    }

    tableItems.value = res.data.ledgers.data;
    totalAmount.value = res.data.total_amount;
    currentPage.value = res.data.ledgers.current_page;
    lastPage.value = res.data.ledgers.last_page;

    setPagination(res.data.ledgers);
  } catch (err) {
      showError('Something is wrong. Check your connectivity!!');
  } finally {
    loading.value = false;
  }
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getLedgerData()
}

const onClickGo = async () => {
  resetPaginationPage(store, route, router)
  await router.isReady()
  await getLedgerData()
}

const exportReport = () => {
  const token = localStorage.getItem('token');
  const url = `${process.env.VUE_APP_BASE_URL}/export/reports/receipt-payment-ledger${getQuery()}&_token=${token}`;
  const a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
}

const onOpenFilter = () => {
  draftFilters.value = Object.assign({}, appliedFilters.value)
}

function setOrDeleteQueryKey (routeQuery, key, value) {
  if (value) {
    routeQuery[key] = value
    return
  }
  delete routeQuery[key]
}

function syncFiltersToRoute () {
  const filters = appliedFilters.value
  const routeQuery = Object.assign({}, route.query)
  setOrDeleteQueryKey(routeQuery, 'account_head_id', filters.account_head_id)
  setOrDeleteQueryKey(routeQuery, 'mop_account_head_id', filters.mop_account_head_id)
  setOrDeleteQueryKey(routeQuery, 'bucket', filters.bucket)
  setOrDeleteQueryKey(routeQuery, 'business_id', filters.business_id)
  setOrDeleteQueryKey(routeQuery, 'cost_centre_id', filters.cost_centre_id)
  setOrDeleteQueryKey(routeQuery, 'project_id', filters.project_id)
  setOrDeleteQueryKey(routeQuery, 'contact_profile_id', filters.contact_profile ? filters.contact_profile.id : null)
  return router.push({path: route.path, query: routeQuery})
}

const onApplyFilters = async () => {
  appliedFilters.value = Object.assign({}, draftFilters.value)
  await syncFiltersToRoute()
  await onClickGo()
}

const onClearFilters = async () => {
  appliedFilters.value = getInitialFilters()
  draftFilters.value = getInitialFilters()
  if (filterPopoverRef.value) filterPopoverRef.value.close()
  await syncFiltersToRoute()
  await onClickGo()
}

onMounted(() => {
  const companyId = route.params.companyId
  const companyQuery = '?company_id=' + companyId
  const dropdownQuery = companyQuery + '&is_business=1&is_project=1&with_donors=1&is_cost_centre=1&is_home=1&is_cash_and_bank_heads=1&is_company_users=1&is_tag_register=1'

  Promise.all([
    fetchAccountHeadsList(companyQuery + '&status=active').then(res => {
      if (res.data) accountHeads.value = res.data
    }),
    fetchDropdownList(dropdownQuery).then(res => {
      if (! res.data) return
      businesses.value = res.data.business_list
      projects.value = res.data.project_list
      costCentres.value = res.data.cost_centre_list
      cashAndBankHeads.value = res.data.cash_and_bank_head_list
      contactList.value = res.data.company_user_list
      tagRegisters.value = res.data.tag_register_list
      if (res.status) homeData.value = res.data.home_data
    }),
  ]).finally(() => {
    getLedgerData()
  })
})
</script>

<style scoped>
@media (max-width: 576px) {
  .table-sm-responsive {
    font-size: 11px;
  }
  .table-sm-responsive th, .table-sm-responsive td {
    padding: 0.5rem 0.25rem !important;
  }
}
</style>
